/**
 * The normalized data behind the text exports (detailed "plan info" sheet and
 * the chart short form).
 *
 * Both formats are organised the way the office's paper forms are — by
 * procedure FAMILY (recall exam, pan, bitewings, scaling, crowns, ortho …) rather
 * than by procedure code — so this module maps the breakdown's rows onto those
 * families and reduces each to the handful of tokens the forms need: a coverage
 * %, a yes/no, a frequency, an age limit. Unknowns are always `BLANK` ("___"),
 * paper-style, never a guess.
 *
 * Pure: props in (the same props the print document takes, plus the breakdown
 * record), plain object out. No DOM, no antd — testable in JSDOM/Node.
 */
import dayjs from "dayjs";

import { classifyEligibility, ELIGIBILITY_LABEL } from "../../../lib/eligibility";
import { UNLIMITED_SENTINEL, describeCategoryValue } from "../../../lib/categoryValue";
import { LINE_CHOICES, PLAN_DETAIL_ROWS, choiceLabel, planDetailText, planYearWord } from "../../../lib/planDetails";
import {
  PROCEDURE_LINES, ageOn, answeredName, compareByCode, matchLineRow, rowCode, rowDescription,
} from "../../../lib/procedureFamilies";
import { fmtDate } from "./printFormat";

export const BLANK = "___";
// A maximum the carrier answered with a percentage but no dollar figure — the
// cards' "No max stated". Distinct from BLANK (nobody asked / nobody knows).
export const NO_MAX_STATED = "no max stated";

const money = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });

// The procedure families and the row-matching live in lib/procedureFamilies.js,
// shared with the Breakdown Worksheet. Re-exported for the older call sites.
export { PROCEDURE_LINES as FAMILIES, matchLineRow as findFamilyRow, rowCode, rowDescription };

// ---- row tokens --------------------------------------------------------------

export function rowPct(row) {
  if (!row || row.data_unavailable) return null;
  const v = row.percentage_covered;
  if (v == null || v === "") return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

function rowText(row) {
  if (!row) return "";
  return [row.notes, row.eligible_today].filter(Boolean).map(String).join(" | ");
}

// The stored frequency basis, when one was recorded; rows from before the
// field existed (or "unknown") fall back to reading their notes.
const BASES = new Set(["rolling", "calendar_year", "benefit_year", "lifetime"]);
function basisOf(row) {
  return row && BASES.has(row.frequency_basis) ? row.frequency_basis : null;
}

/** The line's status when it says more than its % ("Estimate required"), else "". */
export function statusLabel(row) {
  const status = row && row.coverage_status;
  if (!status || status === "covered") return "";
  return choiceLabel(LINE_CHOICES.coverage_status, status);
}

/** "Y" / "N" / BLANK — is the family covered at all? */
export function yesNoToken(row) {
  if (!row || row.data_unavailable) return BLANK;
  if (row.coverage_status === "not_covered") return "N";
  const pct = rowPct(row);
  const state = classifyEligibility(row);
  if (pct != null && pct > 0) return "Y";
  if (pct === 0 || state === "not_covered" || state === "not_found") return "N";
  if (state === "eligible" || state === "cooldown") return "Y";
  return BLANK;
}

/** "yes" / "no" / BLANK — the same answer in plain words (short form). */
export function yesNoWord(row) {
  const t = yesNoToken(row);
  return t === "Y" ? "yes" : t === "N" ? "no" : BLANK;
}

/** "80%" / "N" (not covered) / BLANK. */
export function pctToken(row) {
  if (!row || row.data_unavailable) return BLANK;
  if (row.coverage_status === "not_covered") return "N";
  const pct = rowPct(row);
  if (pct != null) return pct > 0 ? `${pct}%` : "N";
  const state = classifyEligibility(row);
  if (state === "not_covered" || state === "not_found") return "N";
  return BLANK;
}

// A window measured from the last service ("once every 9 months based on first
// paid claim", "over a period of 24 months", "6 consecutive months") as opposed
// to one that resets on a date ("per benefit year", "every calendar year").
export function isRollingWindow(row) {
  if (basisOf(row)) return basisOf(row) === "rolling";
  // "rol+ing": FTCs typed "In ROLING months" on 272 prod rows.
  return /rol+ing|consecutive months|based on (the )?(date of the )?first paid|since (the )?last|from the date|of the last|over a period of \d+ months/i.test(rowText(row));
}

export function isCalendarLimit(row) {
  if (basisOf(row)) return basisOf(row) === "calendar_year" || basisOf(row) === "benefit_year";
  return /calendar year|benefit year|plan year|per year|each year/i.test(rowText(row));
}

export function isLifetimeOnce(row) {
  if (!row) return false;
  const n = Number(row.max_occurrences);
  if (basisOf(row)) return basisOf(row) === "lifetime" && (n === 1 || !row.coverage_duration_months);
  const text = rowText(row);
  // "adults once per lifetime, children once per calendar year" is a split the
  // tokens cannot express; leave the frequency blank and let the note carry it.
  const ambiguous = /lifetime/i.test(text) && /calendar year|benefit year|per year|every year/i.test(text);
  // `max_period` is the DOLLAR maximum's period (a $10,000 lifetime plan stamps
  // every row "lifetime"); it only means a once-in-a-lifetime frequency when the
  // row says "1 occurrence" and carries no window of its own.
  const lifetime = (row.max_period === "lifetime" && !row.coverage_duration_months && n === 1)
    || (/lifetime/i.test(text) && !ambiguous);
  return lifetime && (n === 1 || !row.coverage_duration_months);
}

/** Breakdowns keyed by a person (FTC / office) rather than fetched from a portal. */
export function isManualSource(source) {
  const s = String(source || "").toLowerCase();
  return !(s === "ai_scraper" || s === "ai_phone_call");
}

const WORD_COUNTS = { once: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8 };

/**
 * A frequency read out of free text — what an FTC types when the structured
 * fields stay empty: "Complete exam: once every 2 calendar years", "6 units per
 * calendar year", "recall exam: once every 6 consecutive months", "every 36
 * months (calendar)". Returns the same shape as frequencyOf, or null.
 */
export function frequencyFromText(text) {
  const t = String(text || "");
  // "once every 2 calendar years", "6 units per calendar year", "every 35.5 rolling months"
  const every = /(?:^|[^\d$.])(?:(once|one|two|three|four|five|six|seven|eight|\d+)\s*(?:units?|times?|films?|occurrences?|x)?\s+)?(?:every|per|each)\s+(?:(\d+(?:\.\d+)?)\s+)?(calendar\s+|consecutive\s+|benefit\s+|rolling\s+)?(months?|years?)\b/i;
  // "1 in 24 rolling months", "2 in a rolling year", "1 in 3 rolling years"
  const inWindow = /(?:^|[^\d$.])(\d+)\s+in\s+(?:a\s+|(\d+(?:\.\d+)?)\s+)?(calendar\s+|consecutive\s+|benefit\s+|rolling\s+)?(months?|years?)\b/i;
  let m = every.exec(t);
  let countRaw;
  if (m) {
    countRaw = (m[1] || "").toLowerCase();
  } else {
    m = inWindow.exec(t);
    if (!m) return null;
    countRaw = m[1];
  }
  const count = countRaw ? (WORD_COUNTS[countRaw] != null ? WORD_COUNTS[countRaw] : Number(countRaw)) : 1;
  const n = m[2] ? Number(m[2]) : 1;
  const unit = m[4].toLowerCase();
  const months = unit.indexOf("year") === 0 ? n * 12 : n;
  const qualifier = (m[3] || "").trim().toLowerCase();
  const rolling = qualifier === "consecutive" || qualifier === "rolling" || /rolling|consecutive/i.test(t);
  const calendar = !rolling && (qualifier === "calendar" || qualifier === "benefit" || /calendar year|benefit year/i.test(t));
  if (!count || !months) return null;
  return { count, months, rolling, calendar, fromText: true };
}

/**
 * Structured frequency: { count, months, rolling, unlimited, lifetime,
 * calendar } or null when the row carries no frequency at all. Falls back to
 * the row's free-text note when the structured fields are empty.
 */
export function frequencyOf(row) {
  if (!row || row.data_unavailable) return null;
  const count = row.max_occurrences != null && row.max_occurrences !== "" ? Number(row.max_occurrences) : null;
  const months = row.coverage_duration_months != null && row.coverage_duration_months !== ""
    ? Number(row.coverage_duration_months) : null;
  if (months === 999) return { unlimited: true };
  if (isLifetimeOnce(row)) return { lifetime: true, count: count || 1 };
  if (!count && !months) {
    const text = rowText(row);
    // "adults once per lifetime, children once per calendar year": no single answer.
    if (/lifetime/i.test(text) && /calendar year|benefit year|per year|every year/i.test(text)) return null;
    return frequencyFromText(text);
  }
  const rolling = months ? isRollingWindow(row) : false;
  return {
    count: count || (months ? 1 : null),
    months: months || null,
    rolling,
    // "per benefit year" / "per calendar year": the window resets on a date,
    // not N months after the last visit.
    calendar: !rolling && isCalendarLimit(row),
  };
}

/** Paper token: "24M", "24MR", "4 / 12MR", "2/yr", "1/LTM", "Unlimited", BLANK. */
export function frequencyToken(row) {
  const f = frequencyOf(row);
  if (!f) return BLANK;
  if (f.unlimited) return "Unlimited";
  if (f.lifetime) return `${f.count}/LTM`;
  if (f.calendar && f.months === 12 && f.count > 1) return `${f.count}/yr`;
  if (f.months) {
    const r = f.rolling ? "R" : "";
    return f.count > 1 ? `${f.count} / ${f.months}M${r}` : `${f.months}M${r}`;
  }
  return f.calendar ? `${f.count}/yr` : `${f.count}x`;
}

/** Plain words: "every 24 mo (rolling)", "4 every 12 mo", "2 per year", "once per lifetime". */
export function frequencyWords(row, planYear) {
  const f = frequencyOf(row);
  if (!f) return BLANK;
  if (f.unlimited) return "unlimited";
  if (f.lifetime) return f.count > 1 ? `${f.count} per lifetime` : "once per lifetime";
  if (f.calendar && f.months === 12 && f.count > 1) return `${f.count} per year`;
  if (f.months) {
    const base = f.count > 1 ? `${f.count} every ${f.months} mo` : `every ${f.months} mo`;
    if (f.rolling) return `${base} (rolling)`;
    if (!f.calendar) return base;
    const kind = yearKind(row, planYear);
    return `${base} (${kind === "calendar year" ? "calendar" : kind || "resets yearly"})`;
  }
  return f.calendar ? `${f.count} per year` : `${f.count} times`;
}

/**
 * The year a date-reset limit runs on: the carrier's own word for it, else the
 * plan's year (`planYear`, from planYearWord); "" when neither says. An
 * unqualified "per year" is the plan's year — on a Jun–May plan it is not the
 * calendar.
 */
export function yearKind(row, planYear) {
  if (basisOf(row) === "benefit_year") return "benefit year";
  if (basisOf(row) === "calendar_year") return "calendar year";
  const text = rowText(row);
  if (/benefit year|plan year|policy year/i.test(text)) return "benefit year";
  if (/calendar year/i.test(text)) return "calendar year";
  return planYear || "";
}

/** "8 units / calendar year", "1 unit / 6 consecutive months", "4 units" or BLANK. */
export function scalingUnitsWords(row, planYear) {
  const f = frequencyOf(row);
  if (!f || f.unlimited || f.lifetime) return f && f.unlimited ? "unlimited" : BLANK;
  const units = `${f.count} unit${f.count === 1 ? "" : "s"}`;
  if (f.calendar && f.months === 12) return `${units} / ${yearKind(row, planYear) || "year"}`;
  if (f.months) return `${units} / ${f.months}${f.rolling ? " consecutive" : ""} months`;
  return units;
}

/** Months between recalls: "1 every 9 months" → 9, "2 every 12 months" → 6. */
export function cycleMonths(row) {
  const f = frequencyOf(row);
  if (!f || !f.months) return null;
  const count = f.count || 1;
  return f.months % count === 0 ? f.months / count : f.months;
}

const AGE_LIMIT_RE = /(?:under|to age|up to age|until age|to the age of|age limit(?: of)?|(?:aged?|ages?)\s*\d{1,2}\s*(?:-|–|to)\s*)\s*(?:the age of\s*)?(\d{1,2})\b/i;
const STUDENT_AGE_RE = /student[^.;|]*?(\d{2})\b/i;

/**
 * "18" when the row's own notes carry an age cap, else null. Sentences that
 * appear on every row of the breakdown (`planLevel`) describe the plan, not
 * this procedure, and are ignored here — otherwise "Coverage is limited to
 * children under the age of 21" would become the age limit of every family.
 */
export function ageLimitOf(row, planLevel) {
  // A recorded age rule first: one tier with an upper age is the line's age
  // limit ("to age 18", inclusive); several tiers split the limit by age and
  // are no single cap (recallSplit carries them).
  const tiers = row && Array.isArray(row.age_tiers) ? row.age_tiers : [];
  if (tiers.length > 1) return null;
  if (tiers.length === 1) return tiers[0] && tiers[0].max_age != null ? String(tiers[0].max_age) : null;
  const text = segmentsOf(rowText(row)).filter((seg) => !(planLevel && planLevel.has(seg))).join(" ");
  const m = AGE_LIMIT_RE.exec(text);
  return m ? m[1] : null;
}

const CHILD_AGE_RE = /(?:children|child|dependants?|dependents?)[^.;|]*?(?:under the age of|under|to the age of|to age|up to age)\s*(\d{1,2})\b/i;
const MEMBER_AGE_RE = /(?:member|employee|reaches?|until|to) the age of (\d{1,3})\b/i;

/** Ages the plan states for everyone: dependent children, and the member's own cut-off. */
export function planAges(segments) {
  const text = (segments || []).join(" | ");
  const child = CHILD_AGE_RE.exec(text);
  const member = MEMBER_AGE_RE.exec(text);
  return { childAge: child ? child[1] : null, memberAge: member ? member[1] : null };
}

/**
 * Recall cycles that differ by age, read from the carrier's own wording:
 *   Sun Life: "…period of 6 months. This limit only applies to claimants under the age of 19."
 *             "…period of 9 months. This limit only applies to claimants 19 years of age and over."
 *   manual:   "Adults: 9 months; children under 21: 6 months (rolling)"
 * Returns [{ months, who }] or [] when the row has no split.
 */
export function recallSplit(row) {
  const text = rowText(row);
  const out = [];
  const under = /period of (\d+) months\.?\s*this limit only applies to claimants under the age of (\d+)/gi;
  const over = /period of (\d+) months\.?\s*this limit only applies to claimants (\d+) years of age and over/gi;
  let m;
  while ((m = under.exec(text))) out.push({ months: Number(m[1]), who: `under ${m[2]}` });
  while ((m = over.exec(text))) out.push({ months: Number(m[1]), who: `${m[2]}+` });
  const manual = /adults?:\s*(\d+)\s*months?;\s*children under (\d+):\s*(\d+)\s*months?/i.exec(text);
  if (manual) {
    out.push({ months: Number(manual[3]), who: `under ${manual[2]}` });
    out.push({ months: Number(manual[1]), who: `${manual[2]}+` });
  }
  return out.sort((a, b) => a.months - b.months);
}

/** "5" from "Each occurrence is to be separated by at least 5 months", else null. */
export function minGapMonths(row) {
  const m = /separated by at least (\d+) months/i.exec(rowText(row));
  return m ? Number(m[1]) : null;
}

export function studentAgeOf(text) {
  const m = STUDENT_AGE_RE.exec(text || "");
  return m ? m[1] : null;
}

// ---- plan-level tokens --------------------------------------------------------

/** { year, token, words }: "2026 ODA" in 2026 → C / "2026 (current)"; 2025 → -1 / "2025 (1 yr behind)". */
export function feeGuide(label, exportedAt) {
  const raw = label == null ? "" : String(label).trim();
  if (!raw) return { year: null, token: BLANK, words: BLANK };
  const now = dayjs(exportedAt || undefined);
  const m = /(19|20)\d{2}/.exec(raw);
  if (m) {
    const year = Number(m[0]);
    const behind = now.year() - year;
    if (behind <= 0) return { year, token: "C", words: `${year} (current)` };
    return { year, token: `-${behind}`, words: `${year} (${behind} yr behind)` };
  }
  if (/current/i.test(raw)) return { year: null, token: "C", words: "current" };
  if (/one[- ]year lag|previous year|prior year|1 ?(yr|year) behind|year behind|lag/i.test(raw)) {
    return { year: null, token: "-1", words: "1 yr behind" };
  }
  return { year: null, token: raw, words: raw };
}

/**
 * "$25.00" for a plain number, the label verbatim otherwise ("25/50", "$25 per
 * person", "25 50" — two figures are single and family, never one amount).
 */
export function deductibleText(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  const m = /^\$?\s*(\d{1,3}(?:,\d{3})*|\d+)(\.\d+)?$/.exec(raw);
  return m ? money.format(Number(raw.replace(/[$,\s]/g, ""))) : raw;
}

export function specialistFee(policy) {
  const v = policy && policy.specialist_fee_covered;
  if (v === true) return { token: "Y", words: "yes" };
  if (v === false) return { token: "N", words: "no" };
  return { token: BLANK, words: BLANK };
}

function isUnlimited(v) {
  return v === UNLIMITED_SENTINEL || v === 9999999.99 || Number(v) >= 9000000;
}

/** "$1,000.00" / "Unlimited" / BLANK for a category card amount. */
export function amountToken(value) {
  if (value == null || value === "") return BLANK;
  if (isUnlimited(value)) return "Unlimited";
  const n = Number(value);
  return isFinite(n) ? money.format(n) : String(value);
}

function categoryOf(categories, key) {
  return (categories && categories[key]) || {};
}

function sharesGroup(groups, a, b) {
  return (groups || []).some((g) => Array.isArray(g)
    && g.some((x) => String(x).toLowerCase() === a)
    && g.some((x) => String(x).toLowerCase() === b));
}

function personName(first, last) {
  return [first, last].filter(Boolean).join(" ").trim() || null;
}

function samePerson(a, b) {
  return !!a && !!b && a.replace(/\s+/g, " ").trim().toLowerCase() === b.replace(/\s+/g, " ").trim().toLowerCase();
}

const GENERIC_MAILBOXES = new Set(["info", "admin", "office", "contact", "hello", "support", "frontdesk", "reception", "mail", "team"]);

/**
 * A person's name fit for a sign-off. Real names pass through; an email local
 * part ("ali.daneshi2007", "kooshag77") is prettified; generic mailboxes
 * ("info", "admin") name nobody and yield null.
 */
export function prettyPersonName(name) {
  const raw = String(name == null ? "" : name).trim();
  if (!raw) return null;
  if (/\s/.test(raw)) return raw;
  const words = raw.replace(/\d+/g, " ").split(/[._\-\s]+/).filter(Boolean);
  if (!words.length || (words.length === 1 && GENERIC_MAILBOXES.has(words[0].toLowerCase()))) return null;
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

/** "ProviderConnect" | "Sun Life portal" | "AI call" | "Jane D., manual" | "manual". */
export function sourceLabel(breakdown, policy) {
  const src = breakdown && breakdown.source ? String(breakdown.source) : "";
  if (src === "ai_scraper" || src === "auto") {
    const portal = policy && policy.carrier_portal_name;
    if (portal) return portal;
    const carrier = policy && (policy.insurance_name_display || policy.insurance_name);
    return carrier ? `${carrier} portal` : "portal";
  }
  if (src === "ai_phone_call") return "AI call";
  const who = prettyPersonName(breakdown && breakdown.created_by_name);
  return who ? `${who}, manual` : "manual";
}

// ---- exceptions -----------------------------------------------------------------
//
// A carrier's notes mix three things: the frequency / maximum the tokens already
// express, boilerplate every row carries, and the actual rules of THIS plan. Only
// the last kind is an "exception". Notes are cut into sentences, the routine
// ones dropped, and identical sentences across codes are grouped so "Estimate
// required" prints once with its codes instead of seventeen times.

// ProviderConnect writes one labelled statement per "|" part ("Maximum: (NLMAX)
// $800 maximum every calendar year, including Endodontics (incl. GE); …"). Those
// are routine as a whole and must not be cut into sentences — "(incl. GE)" would
// otherwise shed fragments like "GE); Periodontics(incl.".
const LABELLED_PART_RE = /^(maximum|frequency|fee guide|copay|co-pay|coinsurance|co-insurance)\s*[:=]/i;
const ABBREVIATIONS = /\b(incl|excl|e\.g|i\.e|vs|etc|approx|min|max|mo|yr|yrs|dr|no|st|ave|rd)\.$/i;

/** Sentences/segments of a note ("|"-joined parts, then sentence boundaries). */
export function segmentsOf(text) {
  const parts = String(text || "").split(/\s*\|\s*/);
  const out = [];
  parts.forEach((part) => {
    const cleaned = part.replace(/\s+/g, " ").trim();
    if (!cleaned) return;
    if (LABELLED_PART_RE.test(cleaned)) { out.push(cleaned); return; }
    // Sentence split on ". " before a capital, except after an abbreviation.
    const pieces = [];
    let buf = "";
    cleaned.split(/(?<=[.!?])\s+(?=[A-Z(])/).forEach((piece) => {
      if (buf && ABBREVIATIONS.test(buf)) buf = `${buf} ${piece}`;
      else { if (buf) pieces.push(buf); buf = piece; }
    });
    if (buf) pieces.push(buf);
    pieces.forEach((seg) => {
      const t = seg.replace(/^\s*limits?\s*-\s*\d+\s*:\s*/i, "").trim().replace(/[;,]+$/, "");
      if (t) out.push(t);
    });
  });
  return out;
}

// Always noise: eligibility chatter and the carriers' fine print.
const BOILERPLATE_RES = [
  /^fee guide:/i,
  /^benefits are based on the reasonable cost/i,
  /^(yes, )?the expense is covered\.?$/i,
  /^no, the expense is not eligible today\.?$/i,
  /^the coverage information presented assumes/i,
  /^note: in the event that a maximum is reached/i,
  /^tooth code and surface required/i,
  /^get eligibility\.?$/i,
  /^eligibility info not available\.?$/i,
  /^the coverage is too complex to display\.?$/i,
  /^the procedure code selected is not covered\.?$/i,
  /^you aren'?t covered for this service/i,
  /^not covered\.?$/i,
  /^no coverage\.?$/i,
  /^(not )?eligible( today)?\.?$/i,
  /^(paid \$[\d,.]+\s*\|?\s*)?eligible as of:/i,
  /^paid \$/i,
  /^(copay|co-pay|coinsurance|co-insurance)\s*=/i,
  /^paid based on the standard procedure code cost/i,
  /^claim adjusted (based on other claims|on a prorated basis|to match the plan)/i,
  /^not covered because you'?ve reached the allowed number of uses/i,
  /^no frequency limit/i,
  /^this procedure code is eligible\.?$/i,
  // Age qualifiers of a frequency limit: the recall/scaling tokens already carry
  // the split ("6M under 19 / 9M 19+"); on their own these sentences say nothing.
  /^this limit only applies to claimants (under the age of \d+|\d+ years of age and over)\.?$/i,
];

// Noise only when the row's tokens already say it (a structured frequency).
const FREQUENCY_RES = [
  /^frequency:/i,
  /^maximum of \d+ occurrences?\.?$/i,
  /^coverage limits shown are per person/i,
  /^(consecutive|calendar) months \([a-z /-]+\)$/i,
  /^(rolling|calendar|consecutive|benefit) (months?|years?)\.?$/i,
  /^(calendar|benefit) year\.?$/i,
  // "Complete oral exam: 1 in 24 rolling months", "2 in a rolling year"
  /^(?:[a-z &/-]+:\s*)?\d+ in (?:a |\d+(?:\.\d+)? )?(?:rolling |calendar |consecutive )?(?:months?|years?)\.?$/i,
  // A bare frequency statement ("Once every 36 months (calendar)", "Complete
  // exam: once every 2 calendar years.", "4 units per calendar year"). Anything
  // with more in the sentence is kept.
  /^(?:[a-z &/-]+:\s*)?(?:once|\d+(?:\.\d+)?|half|one|two|three|four)\s*(?:units?|films?|occurrences?|times?)?\s*(?:every|per|each)\s*(?:\d+(?:\.\d+)?)?\s*(?:calendar|consecutive|benefit|rolling)?\s*(?:months?|years?|year|month)(?:\s*\((?:calendar|rolling|consecutive)\))?\.?$/i,
];

// Noise only when the row's tokens already say it (a structured maximum).
const MAXIMUM_RES = [
  /^maximum:/i,
  /^this (maximum|limit) is shared with other procedures/i,
];

/**
 * Is `seg` a routine statement for a row with `ctx` = { hasFrequency, hasMax }?
 * Without a context (the older call sites and tests) both are assumed true.
 */
export function isRoutineSegment(seg, ctx) {
  const t = String(seg || "").trim();
  if (!t) return true;
  const hasFrequency = !ctx || ctx.hasFrequency !== false;
  const hasMax = !ctx || ctx.hasMax !== false;
  if (BOILERPLATE_RES.some((re) => re.test(t))) return true;
  if (hasFrequency && FREQUENCY_RES.some((re) => re.test(t))) return true;
  if (hasMax && MAXIMUM_RES.some((re) => re.test(t))) return true;
  return false;
}

/** The parts of a note that are exceptions, not routine statements. */
export function exceptionParts(text, ctx) {
  return segmentsOf(text).filter((seg) => !isRoutineSegment(seg, ctx));
}

/**
 * What a row already expresses through its tokens — the notes need not repeat
 * it. `cardMax` says which categories' cards carry a maximum (a ProviderConnect
 * "Maximum:" clause is stated on the card even when the row itself has none).
 */
export function rowContext(row, cardMax) {
  const cat = row && row.coverage_type ? String(row.coverage_type).toLowerCase() : "";
  return {
    hasFrequency: !!frequencyOf(row),
    hasMax: !!(row && row.overall_max != null && row.overall_max !== "") || !!(cardMax && cardMax[cat]),
  };
}

export function isExceptionNote(text) {
  return exceptionParts(text).length > 0;
}

function normKey(seg) {
  return seg.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/**
 * Group exception sentences across rows. Returns
 *   { planLevel: Set<segment>, items: [{ text, codes, all }] }
 * where `planLevel` holds sentences that appear on (nearly) every answered row —
 * they describe the plan and are printed once as "all codes".
 */
export function collectExceptions(rows, cardMax) {
  const answered = (rows || []).filter((r) => r && !r.data_unavailable);
  const perRow = answered.map((r) => {
    const segs = [];
    const seen = new Set();
    const ctx = rowContext(r, cardMax);
    exceptionParts(r.notes, ctx).concat(exceptionParts(r.eligible_today, ctx)).forEach((seg) => {
      const k = normKey(seg);
      if (k && !seen.has(k)) { seen.add(k); segs.push(seg); }
    });
    return { code: rowCode(r), segs };
  });
  const counts = {};
  const first = {};
  perRow.forEach(({ segs }) => segs.forEach((seg) => {
    const k = normKey(seg);
    counts[k] = (counts[k] || 0) + 1;
    if (!first[k]) first[k] = seg;
  }));
  const n = perRow.length;
  const threshold = Math.max(3, Math.ceil(n * 0.6));
  const planLevelKeys = new Set(Object.keys(counts).filter((k) => n >= 3 && counts[k] >= threshold));
  const planLevel = new Set(Object.keys(first).filter((k) => planLevelKeys.has(k)).map((k) => first[k]));

  const items = [];
  const byKey = {};
  perRow.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })).forEach(({ code, segs }) => {
    segs.forEach((seg) => {
      const k = normKey(seg);
      if (planLevelKeys.has(k)) return;
      if (!byKey[k]) { byKey[k] = { text: seg, codes: [], all: false }; items.push(byKey[k]); }
      if (byKey[k].codes.indexOf(code) < 0) byKey[k].codes.push(code);
    });
  });
  const planItems = Array.from(planLevel).map((text) => ({ text, codes: [], all: true }));
  return { planLevel, items: planItems.concat(items) };
}

// ---- the plan note -----------------------------------------------------------------
//
// The per-policy note is where an FTC records what the carrier said on the phone
// — "Combined Maximum: $1500 per calendar year", "Deductible $25 person / $50
// family", "This policy is NOT ACTIVE". For manual breakdowns it usually holds
// more than the rows do, so it is printed in full and, where the cards are
// blank, read for the maxima it states.

const INACTIVE_RE = /\b(not active|inactive|terminated|cancell?ed|not in force|no longer (active|covered|in force)|coverage (has )?ended|lapsed)\b/i;

export function planNoteOf(policy) {
  const text = String((policy && policy.attention_note) || "").replace(/\r/g, "").trim();
  if (!text) return null;
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  return {
    text,
    lines,
    date: fmtDate(policy.attention_note_set_at),
    active: INACTIVE_RE.test(text) ? false : null,
  };
}

const MONEY_RE = /\$\s?(\d[\d,]*(?:\.\d+)?)/g;

/**
 * Maxima stated in the note, by category, e.g. "Routine + Major Maximum: $1500
 * combined per person per calendar year" → { basic, preventative, major: "$1,500.00",
 * combined: true }. Conservative: only sentences that name a maximum, never a
 * deductible sentence; a "combined" maximum with no category named is the usual
 * basic+major pool.
 */
export function maximaFromNote(text) {
  const out = { combined: false, period: null };
  const sentences = String(text || "").split(/\n+|(?<=[.!?])\s+(?=[A-Z$])/).map((x) => x.trim()).filter(Boolean);
  sentences.forEach((sent) => {
    if (/deductible/i.test(sent)) return;
    if (!/\bmax(imum)?s?\b/i.test(sent)) return;
    const amounts = [];
    let m;
    MONEY_RE.lastIndex = 0;
    while ((m = MONEY_RE.exec(sent))) amounts.push(Number(m[1].replace(/,/g, "")));
    // "Separate maximums for basic and major" is an answer too — not combined —
    // and it must win over the "basic and major" wording it shares with a pool.
    const separate = /separate(ly)?|not combined|individual maximum|own maximum/i.test(sent);
    const combined = !separate && /combined|shared|routine (and|&|\+) major|basic (and|&|\+) major/i.test(sent);
    if (combined) out.combined = true;
    if (separate) out.separate = true;
    if (/calendar year|benefit year|per year|\/\s*y(ear|r)?\b|annual/i.test(sent)) out.period = out.period || "annual";
    if (/lifetime/i.test(sent)) out.period = out.period || "lifetime";
    if (amounts.length !== 1) return;
    const amount = money.format(amounts[0]);
    const cats = [];
    if (/routine|basic|preventive|preventative|diagnostic/i.test(sent)) cats.push("basic", "preventative");
    if (/major/i.test(sent)) cats.push("major");
    if (/ortho/i.test(sent)) cats.push("ortho");
    if (!cats.length && combined) cats.push("basic", "preventative", "major");
    if (!cats.length && /combined|maximum/i.test(sent) && /routine|major|basic/i.test(text)) cats.push("basic", "preventative", "major");
    cats.forEach((c) => { if (!out[c]) out[c] = amount; });
  });
  return out;
}

/** "$25 person / $50 family per calendar year" from the note, when the policy has no deductible. */
export function deductibleFromNote(text) {
  const m = /deductible[:\s-]*(\$?\s?\d[^\n.;]*)/i.exec(String(text || ""));
  return m ? m[1].trim() : null;
}

/**
 * The scraper's policy-level summary (`breakdown.text.policy_summary`) keeps
 * the carrier's own answer for the deductible, including an explicit "none" —
 * which is worth printing: a blank asks the office to find out, "None" tells it.
 */
export function deductibleFromSummary(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^(none|no deductible|nil|n\/?a|not applicable|no|\$?\s*0(\.00)?)$/i.test(raw)) return "None";
  return deductibleText(raw);
}

/**
 * The scraper's policy-level summary. `InsuranceBreakdown.text` is a TextField
 * holding the LLM output as a JSON string ({"summary": [...], "policy_summary":
 * {deductible, fee_guide, benefit_year_start/end, specialist_fees_covered,
 * age_limit}}); manual breakdowns have none. Never throws on odd content.
 */
export function policySummaryOf(breakdown) {
  let text = breakdown && breakdown.text;
  if (typeof text === "string") {
    try { text = JSON.parse(text); } catch (e) { return {}; }
  }
  const ps = text && typeof text === "object" ? text.policy_summary : null;
  return ps && typeof ps === "object" && !Array.isArray(ps) ? ps : {};
}

/** Every recorded plan detail as display words, keyed by field ("" when never recorded). */
export function planDetailsOf(policy) {
  const out = {};
  PLAN_DETAIL_ROWS.forEach((row) => {
    out[row.field] = planDetailText(row, policy ? policy[row.field] : null);
  });
  return out;
}

/** A whole-number age out of a policy field or a summary value ("21", "to age 21", 21). */
function ageNumber(value) {
  if (value == null || value === "") return null;
  const m = /(\d{1,3})/.exec(String(value));
  return m ? Number(m[1]) : null;
}

// ---- the model ----------------------------------------------------------------

export function buildBreakdownExportModel({
  patient,
  policy,
  officeIdentity,
  categories,
  sharedMaxGroups,
  maxResetDate,
  rows,
  unavailableRows,
  breakdown,
  exportedAt,
}) {
  const pt = patient || {};
  const pol = policy || {};
  const bd = breakdown || {};
  const answered = (rows || []).filter((r) => !r.data_unavailable);
  const allRows = answered.concat((unavailableRows || []).filter((r) => r.data_unavailable));

  const patientName = personName(pt.first_name, pt.last_name) || "Patient";
  const memberName = personName(pol.first_name, pol.last_name);
  const relation = pol.relation_to_member_display || pol.relation_to_member || null;
  const isSelf = /^self$/i.test(String(relation || "")) || samePerson(memberName, patientName);

  const fg = feeGuide(pol.fee_guide, exportedAt);
  const basic = categoryOf(categories, "basic");
  const major = categoryOf(categories, "major");
  const prev = categoryOf(categories, "preventative");
  const ortho = categoryOf(categories, "orthodontics");

  const cardMax = {
    preventative: !!(prev.overall_max != null && prev.overall_max !== ""),
    basic: !!(basic.overall_max != null && basic.overall_max !== ""),
    major: !!(major.overall_max != null && major.overall_max !== ""),
    orthodontics: !!(ortho.overall_max != null && ortho.overall_max !== ""),
  };
  const { planLevel, items: exceptionItems } = collectExceptions(answered, cardMax);
  const ages = planAges(Array.from(planLevel));
  const planNote = planNoteOf(pol);
  const noteMax = maximaFromNote(planNote ? planNote.text : "");
  const manual = isManualSource(bd.source);
  const planYear = planYearWord(pol);
  const summary = policySummaryOf(bd);
  // The complete exam is coded by dentition, so the patient's age picks the code.
  const age = ageOn(pt.birth_date || pt.date_of_birth, exportedAt);
  // The policy row's age_limit first; the scraper's policy summary keeps the
  // same answer for breakdowns whose policy row never received it.
  const ageLimitNum = ageNumber(pol.age_limit) != null ? ageNumber(pol.age_limit) : ageNumber(summary.age_limit);

  const families = PROCEDURE_LINES.map((line) => {
    const row = matchLineRow(allRows, line, { age });
    const code = row ? rowCode(row) : line.templateCode;
    return {
      key: line.key,
      label: line.label,
      templateCode: line.templateCode,
      code,
      // Named only when the code that answered is not the paper form's own —
      // "Denture (51101 · answered as 53201 partial denture, cast, upper)".
      answeredName: row && code !== line.templateCode ? answeredName(line, row) : null,
      description: row ? rowDescription(row) : "",
      row,
      pct: pctToken(row),
      yesNo: yesNoToken(row),
      yesNoWord: yesNoWord(row),
      frequency: frequencyToken(row),
      frequencyWords: frequencyWords(row, planYear),
      cycleMonths: cycleMonths(row),
      state: row && !row.data_unavailable ? classifyEligibility(row) : null,
      stateLabel: row && !row.data_unavailable ? ELIGIBILITY_LABEL[classifyEligibility(row)] : null,
      status: row && !row.data_unavailable ? statusLabel(row) : "",
      eligibleAgain: row ? fmtDate(row.eligible_again_on) : null,
      ageLimit: ageLimitOf(row, planLevel),
      recallSplit: row ? recallSplit(row) : [],
      minGapMonths: row ? minGapMonths(row) : null,
      calendar: row ? !!(frequencyOf(row) && frequencyOf(row).calendar) : false,
      scalingUnits: scalingUnitsWords(row, planYear),
      basicOrMajor: row && row.coverage_type && /^(basic|major)$/i.test(row.coverage_type) ? row.coverage_type : null,
      unavailable: !!(row && row.data_unavailable),
    };
  });
  const byKey = {};
  families.forEach((f) => { byKey[f.key] = f; });

  const exceptions = exceptionItems;

  const allText = answered.map(rowText).join(" | ");
  const recordedAt = bd.upload_date || bd.created_at || null;

  return {
    exportedAt: exportedAt || new Date().toISOString(),
    recordedAt,
    patient: { name: patientName, dob: fmtDate(pt.birth_date || pt.date_of_birth) },
    member: { name: memberName, dob: fmtDate(pol.date_of_birth), relation, isSelf },
    carrier: {
      name: pol.insurance_name_display || pol.insurance_name || pol.carrier_display_name || null,
      phone: pol.carrier_phone_number || null,
      portal: pol.carrier_portal_name || null,
      ordinal: pol.insurance_type_display || pol.insurance_type || null,
    },
    office: officeIdentity ? {
      name: officeIdentity.office_name || null,
      phone: officeIdentity.office_contact_number || null,
    } : null,
    plan: {
      // FTCs sometimes type the member's name into the plan-name field; a
      // person is not a plan, so that never reaches "Employer / Plan".
      planName: pol.plan_name && !samePerson(pol.plan_name, patientName) && !samePerson(pol.plan_name, memberName)
        ? pol.plan_name : null,
      groupNumber: pol.contract_number || null,
      memberId: pol.member_id || null,
      feeGuide: pol.fee_guide || null,
      feeGuideToken: fg.token,
      feeGuideWords: fg.words,
      specialistFee: specialistFee(pol),
      deductible: deductibleText(pol.deductible)
        || (planNote ? deductibleFromNote(planNote.text) : null)
        || deductibleFromSummary(summary.deductible),
      // The period containing today (server `benefit_period`, a stored year
      // rolled forward), so an export never prints a year already over — nor
      // a past start date as the renewal.
      benefitYearStart: fmtDate((pol.benefit_period || {}).start || pol.benefit_year_start),
      benefitYearEnd: fmtDate((pol.benefit_period || {}).end || pol.benefit_year_end),
      renews: fmtDate((pol.benefit_period || {}).resets) || maxResetDate || null,
      // The policy's single age_limit can be either a dependent-child cap or the
      // member's own cut-off (retiree plans end at 80): the plan-level wording
      // says which. Only a children/dependants sentence feeds "Dep. Child".
      ageLimit: ageLimitNum != null ? String(ageLimitNum) : null,
      depChildAge: (pol.dependent_age_limit != null ? String(pol.dependent_age_limit) : null)
        || ages.childAge || (ageLimitNum != null && ageLimitNum <= 30 ? String(ageLimitNum) : null),
      memberAge: ages.memberAge || (ageLimitNum != null && ageLimitNum > 30 ? String(ageLimitNum) : null),
      studentAge: (pol.student_age_limit != null ? String(pol.student_age_limit) : null) || studentAgeOf(allText),
      // The recorded plan details, as words ("" = never recorded).
      details: planDetailsOf(pol),
    },
    maxima: (() => {
      const cat = (c, key) => {
        const hasPct = c.coverage != null && c.coverage !== "";
        // The carrier's answer when it gave no % (every Major line of an OTIP
        // plan: "Predetermination required"), or the office's own.
        const status = c.status ? choiceLabel(LINE_CHOICES.coverage_status, c.status) : null;
        const structured = amountToken(c.overall_max);
        const fromNote = structured === BLANK && noteMax[key] ? noteMax[key] : null;
        // The cards' own words for an empty dollar figure: a (non-zero) percentage
        // with no maximum is "No max stated" (Sun Life prints "Overall maximum:
        // None"); nothing at all stays a paper blank for the office to fill.
        const noMaxStated = structured === BLANK && !fromNote
          && describeCategoryValue("Overall Max", c.overall_max, false, hasPct && Number(c.coverage) > 0).text === "No max stated";
        return {
          max: fromNote || (noMaxStated ? NO_MAX_STATED : structured),
          fromNote: !!fromNote,
          noMaxStated,
          remaining: amountToken(c.benefits_remaining),
          pct: hasPct ? `${c.coverage}%` : BLANK,
          status,
          note: c.note || null,
          // What the Coverage cell says: the %, else the status, else a blank.
          words: hasPct ? `${c.coverage}%` : status || BLANK,
          // Not covered at all: 0%, or said so with no % given.
          off: hasPct ? Number(c.coverage) === 0 : c.status === "not_covered",
        };
      };
      const orthoCat = cat(ortho, "ortho");
      orthoCat.lifetime = ortho.period === "lifetime" || (orthoCat.fromNote && noteMax.period === "lifetime");
      // Y when basic and major draw on one pool; N when the pools were worked
      // out (or the note says separate) and they don't; null when nobody knows.
      const computed = Array.isArray(sharedMaxGroups) && sharedMaxGroups.length > 0;
      const combined = sharesGroup(sharedMaxGroups, "basic", "major") || noteMax.combined
        ? true
        : computed || noteMax.separate ? false : null;
      return {
        preventative: cat(prev, "preventative"),
        basic: cat(basic, "basic"),
        major: cat(major, "major"),
        ortho: orthoCat,
        combined,
      };
    })(),
    planNote,
    families,
    family: byKey,
    exceptions,
    unavailable: (unavailableRows || []).slice().sort(compareByCode).map(rowCode).filter(Boolean),
    provenance: {
      isManual: manual,
      date: fmtDate(recordedAt),
      dateShort: recordedAt && dayjs(String(recordedAt).split("T")[0]).isValid()
        ? dayjs(String(recordedAt).split("T")[0]).format("MMM D/YY") : null,
      sourceLabel: sourceLabel(bd, pol),
    },
  };
}
